// Phase 82 — Mini-sites Pro : registre des sections (libellés, champs d'édition, valeurs par défaut).
// Le backend valide les types (routes/web/templates.py SECTION_TYPES) ; ici on décrit l'édition et l'affichage.

export const FONT_OPTIONS = [
  { value: "moderne", label: "Moderne (géométrique)", css: "'Space Grotesk', 'Manrope', ui-sans-serif, system-ui, sans-serif" },
  { value: "classique", label: "Classique (serif)", css: "Georgia, 'Times New Roman', serif" },
  { value: "elegant", label: "Élégant (humaniste)", css: "'Manrope', 'Segoe UI', ui-sans-serif, sans-serif" },
  { value: "mono", label: "Technique (monospace)", css: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" },
];

export const BUTTON_OPTIONS = [
  { value: "sharp", label: "Angles vifs" },
  { value: "rounded", label: "Arrondis" },
  { value: "pill", label: "Pilule" },
];

const F = (key, label, type = "text", extra = {}) => ({ key, label, type, ...extra });

export const SECTION_TYPES = {
  hero: {
    label: "Accueil (bandeau)", icon: "Sparkles", anchor: "accueil", unique: true,
    help: "La première chose que voient vos visiteurs : un titre fort, une phrase et un bouton d'action.",
    fields: [F("headline", "Titre principal"), F("text", "Phrase d'accroche", "textarea"), F("image_url", "Image de fond", "image"),
      F("cta_label", "Texte du bouton"), F("cta_link", "Lien du bouton", "text", { placeholder: "#contact, #boutique ou https://…" }),
      F("align", "Alignement", "select", { options: [{ value: "left", label: "À gauche" }, { value: "center", label: "Centré" }] })],
  },
  about: {
    label: "À propos", icon: "Info", anchor: "a-propos",
    help: "Votre histoire, vos valeurs, ce qui vous distingue. Ajoutez 3 points forts.",
    fields: [F("text", "Texte de présentation", "textarea", { rows: 6 }), F("image_url", "Photo", "image"), F("highlights", "Points forts", "tags", { placeholder: "Ex. Devis gratuit" })],
  },
  services: {
    label: "Prestations / services", icon: "Briefcase", anchor: "prestations",
    help: "Listez vos prestations avec un prix indicatif. Le bouton ouvre le formulaire de contact pré-rempli.",
    fields: [F("items", "Prestations", "list", { itemLabel: "label", fields: [F("label", "Nom"), F("description", "Description", "textarea"), F("price", "Prix (€, 0 = sur devis)", "number"), F("duration", "Durée / format")] }),
      F("show_prices", "Afficher les prix", "toggle"), F("cta_label", "Texte du bouton")],
  },
  shop: {
    label: "Boutique", icon: "ShoppingBag", anchor: "boutique", unique: true,
    help: "Affiche vos produits (onglet Boutique) avec panier et commande. Activez la boutique dans l'onglet Boutique.",
    fields: [F("intro", "Texte d'introduction", "textarea"), F("columns", "Colonnes", "select", { options: [{ value: 2, label: "2" }, { value: 3, label: "3" }, { value: 4, label: "4" }] })],
  },
  gallery: {
    label: "Galerie photos", icon: "Images", anchor: "galerie",
    help: "Jusqu'à 24 photos. Formats JPG, PNG ou WEBP (8 Mo max).",
    fields: [F("images", "Photos", "images"), F("layout", "Disposition", "select", { options: [{ value: "grid", label: "Grille" }, { value: "masonry", label: "Mosaïque" }] })],
  },
  team: {
    label: "Équipe", icon: "Users", anchor: "equipe",
    help: "Présentez les personnes derrière votre structure.",
    fields: [F("members", "Membres", "list", { itemLabel: "name", fields: [F("name", "Nom"), F("role", "Rôle"), F("photo_url", "Photo", "image"), F("bio", "Courte présentation", "textarea")] })],
  },
  reviews: {
    label: "Avis & témoignages", icon: "Star", anchor: "avis",
    help: "Entreprises : vos avis clients vérifiés PAG peuvent s'afficher automatiquement. Sinon, saisissez des témoignages.",
    fields: [F("source", "Source", "select", { options: [{ value: "manual", label: "Témoignages saisis" }, { value: "pag", label: "Avis clients vérifiés PAG (entreprises)" }, { value: "both", label: "Les deux" }] }),
      F("items", "Témoignages", "list", { itemLabel: "author", fields: [F("author", "Auteur"), F("text", "Témoignage", "textarea"), F("rating", "Note (1-5)", "number", { min: 1, max: 5 })] })],
  },
  faq: {
    label: "Questions fréquentes", icon: "HelpCircle", anchor: "faq",
    help: "Répondez aux questions que l'on vous pose le plus souvent.",
    fields: [F("items", "Questions", "list", { itemLabel: "q", fields: [F("q", "Question"), F("a", "Réponse", "textarea")] })],
  },
  hours: {
    label: "Horaires & accès", icon: "Clock", anchor: "horaires",
    help: "Horaires d'ouverture, adresse et lien de carte (Google Maps).",
    fields: [F("days", "Horaires", "hours"), F("address", "Adresse"), F("map_url", "Lien Google Maps", "url"), F("note", "Information complémentaire", "textarea")],
  },
  news: {
    label: "Actualités", icon: "Newspaper", anchor: "actualites",
    help: "Associations et syndicats : vos actualités publiées dans votre espace s'affichent automatiquement.",
    fields: [F("source", "Source", "select", { options: [{ value: "manual", label: "Saisies ici" }, { value: "auto", label: "Automatique (mon espace PAG)" }] }),
      F("items", "Actualités", "list", { itemLabel: "title", fields: [F("title", "Titre"), F("date", "Date", "date"), F("text", "Texte", "textarea"), F("image_url", "Image", "image")] })],
  },
  contact: {
    label: "Contact", icon: "Mail", anchor: "contact", unique: true,
    help: "Coordonnées et formulaire : chaque message vous est envoyé par email et apparaît dans l'onglet Messages.",
    fields: [F("intro", "Texte d'introduction", "textarea"), F("email", "Email affiché"), F("phone", "Téléphone affiché"), F("address", "Adresse"), F("form_enabled", "Formulaire de contact", "toggle")],
  },
  cta: {
    label: "Bandeau d'appel à l'action", icon: "Megaphone", anchor: "action",
    help: "Un message fort avec un bouton (promotion, inscription, don…).",
    fields: [F("headline", "Titre"), F("text", "Texte", "textarea"), F("button_label", "Texte du bouton"), F("button_link", "Lien du bouton"),
      F("style", "Style", "select", { options: [{ value: "dark", label: "Sombre" }, { value: "primary", label: "Couleur principale" }, { value: "light", label: "Clair" }] })],
  },
  text: {
    label: "Texte libre", icon: "AlignLeft", anchor: "texte",
    help: "Un bloc de texte (paragraphes séparés par une ligne vide).",
    fields: [F("body", "Texte", "textarea", { rows: 10 })],
  },
  video: {
    label: "Vidéo", icon: "PlayCircle", anchor: "video",
    help: "Collez un lien YouTube ou Vimeo.",
    fields: [F("url", "Lien de la vidéo", "url"), F("caption", "Légende")],
  },
  pag_offers: {
    label: "Formules PAG (partenaire)", icon: "Gem", anchor: "offres", unique: true, kinds: ["bp"],
    help: "Affiche les 4 formules PAG avec votre code partenaire : chaque inscription vous est attribuée.",
    fields: [F("intro", "Texte d'introduction", "textarea")],
  },
  booking: {
    label: "Réservations (mini-site)", icon: "CalendarCheck", anchor: "reservation", unique: true, kinds: ["entreprise"],
    help: "Relie votre site au module de réservation de votre mini-site PAG (dates, hébergements, QR code d'arrivée). Activez la réservation dans « Mini-site public ».",
    fields: [F("intro", "Texte d'introduction", "textarea"), F("button_label", "Texte du bouton"), F("show_info", "Afficher horaires d'arrivée / départ", "toggle")],
  },
  module_form: {
    label: "Formulaire module (demande client)", icon: "MessagesSquare", anchor: "demande", kinds: ["entreprise"],
    help: "Transforme une fiche d'un de vos modules (volet client) en formulaire public : demande d'intervention, de devis, inscription, réclamation… La fiche arrive dans votre module, le client suit son avancement dans son espace client.",
    fields: [F("form_ref", "Fiche du module", "module_entity"), F("intro", "Texte d'introduction", "textarea"), F("button_label", "Texte du bouton (vide = celui du module)"), F("success_message", "Message après envoi", "textarea"), F("show_client_access", "Afficher « Déjà client ? Accéder à mon espace »", "toggle")],
  },
};

export const SECTION_ORDER = ["hero", "about", "services", "shop", "booking", "module_form", "gallery", "team", "reviews", "faq", "hours", "news", "contact", "cta", "text", "video", "pag_offers"];

export function sectionMeta(type) { return SECTION_TYPES[type] || { label: type, icon: "Square", fields: [], anchor: type }; }
export function sectionAnchor(section) { return `${sectionMeta(section.type).anchor}-${section.id}`; }
export function fontCss(key) { return (FONT_OPTIONS.find((f) => f.value === key) || FONT_OPTIONS[0]).css; }
export function radiusFor(key) { return key === "pill" ? "999px" : key === "rounded" ? "10px" : "0px"; }
